import React, { useEffect, useState } from 'react';
import { Activity, Loader2, Plus } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { useOptionalEncounter } from '@/contexts/EncounterContext';
import {
  vitalsService,
  VitalLoinc,
  VitalObservation,
  readVitalNumber,
} from '@/services/vitalsService';
import VitalsCaptureSheet from '../emr/VitalsCaptureSheet';
import EncounterChip from '@/components/visit/EncounterChip';

interface PatientVitalsTabProps {
  patientId: string;
}

interface VitalTile {
  code: string;
  label: string;
  unit: string;
  color: string;
  // For panel-style observations (e.g. BP), the primary and paired
  // numbers live inside `components[]` rather than as separate trend
  // rows. When set, the tile fetches the panel LOINC and reads these
  // component codes from each row.
  componentCodes?: { primary: string; paired?: string };
}

const TILES: VitalTile[] = [
  {
    code: VitalLoinc.BLOOD_PRESSURE_PANEL,
    label: 'Blood Pressure',
    unit: 'mmHg',
    color: '#ef4444',
    componentCodes: { primary: VitalLoinc.SYSTOLIC_BP, paired: VitalLoinc.DIASTOLIC_BP },
  },
  { code: VitalLoinc.HEART_RATE, label: 'Heart Rate', unit: 'bpm', color: '#f97316' },
  { code: VitalLoinc.BODY_WEIGHT, label: 'Weight', unit: 'lb', color: '#10b981' },
  { code: VitalLoinc.BODY_HEIGHT, label: 'Height', unit: 'cm', color: '#6366f1' },
  { code: VitalLoinc.BODY_TEMPERATURE, label: 'Temperature', unit: '°F', color: '#d946ef' },
  { code: VitalLoinc.OXYGEN_SATURATION, label: 'SpO₂', unit: '%', color: '#0ea5e9' },
  { code: VitalLoinc.RESPIRATORY_RATE, label: 'Respiratory Rate', unit: '/min', color: '#14b8a6' },
];

const readComponentNumber = (
  row: VitalObservation | undefined,
  componentCode: string,
): number | null => {
  // `getLatestVitals` returns components shaped `{ code, value }` (the
  // controller rewrites `code_code` → `code`), but the raw `getVitalTrend`
  // rows expose the JSONB column verbatim where the key is `code_code` and
  // the value can live under `value` or `value_quantity_value`. Match both.
  const c = row?.components?.find(
    (c: any) => c.code === componentCode || c.code_code === componentCode,
  ) as any;
  if (!c) return null;
  const raw = c.value ?? c.value_quantity_value;
  if (raw == null) return null;
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  return Number.isFinite(n) ? n : null;
};

const formatDateShort = (s?: string | null) => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PatientVitalsTab = ({ patientId }: PatientVitalsTabProps) => {
  const { clinic } = useClinic();
  const encounterCtx = useOptionalEncounter();
  const [isLoading, setIsLoading] = useState(false);
  const [trends, setTrends] = useState<Record<string, VitalObservation[]>>({});
  const [showCapture, setShowCapture] = useState(false);
  const [captureNonce, setCaptureNonce] = useState(0);

  useEffect(() => {
    if (!clinic?.id || !patientId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const loincCodes = Array.from(new Set(TILES.map(t => t.code)));
        const results = await Promise.all(
          loincCodes.map(async (code) => {
            try {
              const rows = await vitalsService.getVitalTrend(clinic.id, patientId, code, 30);
              return [code, rows] as const;
            } catch {
              return [code, []] as const;
            }
          }),
        );
        if (!cancelled) {
          setTrends(Object.fromEntries(results));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clinic?.id, patientId, captureNonce]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-violet-500">
            <Activity className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Vitals</h3>
            <p className="text-xs text-slate-500">Latest values and trend (up to last 30 readings)</p>
          </div>
        </div>
        {clinic?.id && (
          <Button size="sm" onClick={() => setShowCapture(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Capture vitals
          </Button>
        )}
      </div>

      {clinic?.id && (
        <VitalsCaptureSheet
          open={showCapture}
          onOpenChange={setShowCapture}
          clinicId={clinic.id}
          encounterId={encounterCtx?.encounterId}
          patientId={patientId}
          onRecorded={() => {
            setShowCapture(false);
            setCaptureNonce(n => n + 1);
          }}
        />
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {TILES.map((tile) => {
          const primaryRows = trends[tile.code] || [];
          const latest = primaryRows[0];
          const isPanel = !!tile.componentCodes;

          const readPrimary = (row?: VitalObservation) =>
            isPanel ? readComponentNumber(row, tile.componentCodes!.primary) : readVitalNumber(row);
          const readPaired = (row?: VitalObservation) =>
            isPanel && tile.componentCodes!.paired
              ? readComponentNumber(row, tile.componentCodes!.paired)
              : null;

          const latestValue = readPrimary(latest);
          const latestPairedValue = readPaired(latest);
          const hasData = primaryRows.length > 0 && latestValue != null;

          if (!hasData) {
            return (
              <div key={tile.code} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900">{tile.label}</span>
                  <span className="text-xs text-slate-400">No readings</span>
                </div>
                <div className="mt-2 text-xs text-slate-400">—</div>
              </div>
            );
          }

          const chartData = primaryRows
            .slice()
            .reverse()
            .map((row) => ({
              when: formatDateShort(row.effective_date_time),
              primary: readPrimary(row),
              paired: readPaired(row),
            }));

          return (
            <div key={tile.code} className="border border-slate-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-slate-500">{tile.label}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-semibold text-slate-900">
                      {latestValue != null ? latestValue.toFixed(1) : '—'}
                      {isPanel && latestPairedValue != null && (
                        <span className="text-2xl text-slate-400"> / {latestPairedValue.toFixed(0)}</span>
                      )}
                    </span>
                    <span className="text-xs text-slate-500">{tile.unit}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  {formatDateShort(latest?.effective_date_time)}
                  {latest?.encounter_id && (
                    <EncounterChip encounterId={latest.encounter_id} label="Visit" />
                  )}
                </div>
              </div>
              {chartData.length > 1 && (
                <div className="mt-4 h-28 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="when" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={{ fontSize: 11, padding: '6px 8px' }}
                        labelStyle={{ fontSize: 10 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="primary"
                        stroke={tile.color}
                        strokeWidth={2}
                        dot={false}
                      />
                      {isPanel && tile.componentCodes!.paired && (
                        <Line
                          type="monotone"
                          dataKey="paired"
                          stroke={tile.color}
                          strokeWidth={2}
                          strokeDasharray="4 2"
                          dot={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PatientVitalsTab;
